import { Controller } from "@hotwired/stimulus";
import { errorDetail, sendJSON } from "../api.js";

// Formularz kontaktowy (/kontakt/): walidacja pól wymaganych → POST /api/contact-messages/
// Strona publiczna — brak sesji jest normą; CSRF z <meta> lub cookie.
// ZERO innerHTML: błędy i komunikaty ustawiane przez textContent.
export default class extends Controller {
  static targets = [
    "form",
    "fullName",
    "fullNameError",
    "company",
    "email",
    "emailError",
    "phone",
    "subject",
    "subjectError",
    "message",
    "messageError",
    "consent",
    "consentError",
    "formError",
    "submitBtn",
    "success",
  ];

  static values = {
    url: String, // POST /api/contact-messages/
  };

  async submit(event) {
    event?.preventDefault?.();

    // Wyczyść poprzednie błędy
    this.#clearErrors();

    // Walidacja pól wymaganych po stronie klienta
    const fullName = this.fullNameTarget.value.trim();
    const email = this.emailTarget.value.trim();
    const subject = this.subjectTarget.value;
    const message = this.messageTarget.value.trim();
    const consent = this.consentTarget.checked;

    let valid = true;

    if (!fullName) {
      this.#fieldError("fullNameError", "Imię i nazwisko jest wymagane.");
      valid = false;
    }
    if (!email) {
      this.#fieldError("emailError", "Adres e-mail jest wymagany.");
      valid = false;
    }
    if (!subject) {
      this.#fieldError("subjectError", "Wybierz temat wiadomości.");
      valid = false;
    }
    if (!message) {
      this.#fieldError("messageError", "Treść wiadomości jest wymagana.");
      valid = false;
    }
    if (!consent) {
      this.#fieldError("consentError", "Zgoda na przetwarzanie danych jest wymagana.");
      valid = false;
    }

    if (!valid) return;

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;

    const company = this.companyTarget.value.trim();
    const phone = this.hasPhoneTarget ? this.phoneTarget.value.trim() : "";

    const res = await sendJSON("POST", this.urlValue, {
      full_name: fullName,
      company,
      email,
      phone,
      subject,
      message,
      consent,
    });

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;

    if (res.ok) {
      // Sukces — schowaj formularz, pokaż komunikat w miejscu bez przeładowania
      if (this.hasFormTarget) this.formTarget.hidden = true;
      if (this.hasSuccessTarget) this.successTarget.hidden = false;
      return;
    }

    // 429 — limit. Treść z API bywa sucha („Limit wyczerpany."), więc zawsze dokładamy
    // wskazówkę co dalej — użytkownik ma wiedzieć, że to stan przejściowy.
    if (res.status === 429) {
      const detail = errorDetail(res.data, "Zbyt wiele wiadomości.");
      this.#formError(`${detail} Odczekaj chwilę i spróbuj później.`);
      return;
    }

    // 400 — błędy pól z API; mapuj pod właściwe inputy
    if (res.status === 400 && res.data && typeof res.data === "object") {
      const mapped = this.#mapApiErrors(res.data);
      if (!mapped) {
        // Brak rozpoznanych błędów pól → ogólny komunikat
        this.#formError(
          errorDetail(res.data, "Nie udało się wysłać wiadomości. Sprawdź formularz."),
        );
      }
      return;
    }

    // Inne błędy serwera
    this.#formError(
      errorDetail(res.data, "Nie udało się wysłać wiadomości. Spróbuj ponownie."),
    );
  }

  // --- prywatne ---

  /** Mapuje błędy pól z odpowiedzi 400. Zwraca true jeśli choć jeden błąd pola zmapowany. */
  #mapApiErrors(data) {
    const fieldMap = {
      full_name: "fullNameError",
      email: "emailError",
      subject: "subjectError",
      message: "messageError",
      consent: "consentError",
    };

    let mapped = false;

    for (const [apiField, targetName] of Object.entries(fieldMap)) {
      if (data[apiField]) {
        const msgs = Array.isArray(data[apiField]) ? data[apiField] : [data[apiField]];
        this.#fieldError(targetName, msgs.join(" "));
        mapped = true;
      }
    }

    // Błędy ogólne (detail, non_field_errors)
    const general = data.detail ?? (data.non_field_errors ?? []).join(" ");
    if (general) {
      this.#formError(String(general));
    }

    return mapped;
  }

  #clearErrors() {
    const errorTargets = [
      "fullNameError",
      "emailError",
      "subjectError",
      "messageError",
      "consentError",
      "formError",
    ];
    for (const name of errorTargets) {
      const target = this[`has${name.charAt(0).toUpperCase() + name.slice(1)}Target`];
      if (target) {
        const el = this[`${name}Target`];
        el.hidden = true;
        el.textContent = "";
      }
    }
  }

  #fieldError(targetName, message) {
    const capitalized = targetName.charAt(0).toUpperCase() + targetName.slice(1);
    const hasTarget = this[`has${capitalized}Target`];
    if (!hasTarget) return;
    const el = this[`${targetName}Target`];
    el.hidden = false;
    el.textContent = message;
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return;
    this.formErrorTarget.hidden = false;
    this.formErrorTarget.textContent = message;
  }
}
